// src/shell/Sidebar.jsx
import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';

// Map application.id → route path
const APP_ID_TO_PATH = {
  'content-generator': '/content-generator',
};

// Fixed items always shown first
const FIXED_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', path: '/dashboard', emoji: '📊' },
  { id: 'credits', label: 'Crediti', path: '/credits', emoji: '💳' },
  {
    id: 'content-generator',
    label: 'Content Generator',
    path: '/content-generator',
    emoji: '✍️',
    children: [
      { id: 'cg-examples', label: 'Carica esempi', path: '/content-generator#esempi' },
    ],
  },
];

function navStyle(isActive) {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    borderRadius: '8px',
    textDecoration: 'none',
    fontSize: '13px',
    fontFamily: "'DM Sans', sans-serif",
    color: isActive ? '#E8002D' : '#3a3a3c',
    background: isActive ? 'rgba(232,0,45,0.08)' : 'transparent',
    fontWeight: isActive ? 600 : 400,
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  };
}

const subNavStyle = {
  display: 'flex',
  alignItems: 'center',
  padding: '5px 12px 5px 36px',
  borderRadius: '8px',
  textDecoration: 'none',
  fontSize: '12px',
  fontFamily: "'DM Sans', sans-serif",
  color: '#6b6b6e',
  fontWeight: 400,
  transition: 'all 0.15s',
  whiteSpace: 'nowrap',
};

export default function Sidebar({ open }) {
  const { applications, isSuperadmin, isTenantAdmin, adminMode } = useAuth();

  const HIDDEN_APP_IDS = new Set(['seo-rewrite']);
  const fixedIds = new Set(FIXED_ITEMS.map(i => i.id));
  const appItems = applications
    .filter(app => !fixedIds.has(app.id) && !HIDDEN_APP_IDS.has(app.id))
    .map(app => ({
      id: app.id,
      label: app.display_name,
      path: APP_ID_TO_PATH[app.id] || `/${app.id}`,
      emoji: app.id === 'content-generator' ? '✍️' : '⚡',
    }));

  // Run history for plain members (non-SA, non-tenant-admin). Tenant-admins and
  // superadmins reach the same view from their own console sections below, so we
  // skip the entry here to avoid a duplicate menu item.
  const runHistoryItem = (!isSuperadmin && !isTenantAdmin)
    ? [{ id: 'runs', label: 'Storico Generazioni', path: '/runs', emoji: '📈' }]
    : [];

  const allItems = [...FIXED_ITEMS, ...appItems, ...runHistoryItem];

  const adminItems = isSuperadmin
    ? [
        { id: 'admin-overview', label: 'Panoramica', path: '/admin/overview', emoji: '📊' },
        { id: 'admin-users', label: 'Gestione utenti', path: '/admin/users', emoji: '🔧' },
        { id: 'admin-runs', label: 'Gestione Run', path: '/admin/runs', emoji: '📈' },
      ]
    : [];

  const tenantAdminItems = (isTenantAdmin && !adminMode && !isSuperadmin)
    ? [
        { id: 'tenant-admin-users', label: 'Gestione utenti', path: '/tenant-admin/users', emoji: '🔧' },
        { id: 'tenant-admin-runs',  label: 'Storico Generazioni',    path: '/tenant-admin/runs',  emoji: '📈' },
      ]
    : [];

  return (
    <>
      <style>{`
        .sidebar {
          width: 220px;
          background: #ffffff;
          border-right: 1px solid rgba(0,0,0,0.08);
          display: flex;
          flex-direction: column;
          padding: 16px 10px;
          flex-shrink: 0;
          transition: transform 0.25s ease;
        }
        .sidebar-nav {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        @media (max-width: 768px) {
          .sidebar {
            position: fixed;
            top: 52px;
            left: 0;
            bottom: 0;
            z-index: 100;
            transform: translateX(-100%);
            box-shadow: none;
          }
          .sidebar.open {
            transform: translateX(0);
            box-shadow: 4px 0 20px rgba(0,0,0,0.1);
          }
        }
      `}</style>
      <aside className={`sidebar${open ? ' open' : ''}`}>
        <nav className="sidebar-nav">
          {!adminMode && allItems.map(item => (
            <React.Fragment key={item.id}>
              <NavLink to={item.path} style={({ isActive }) => navStyle(isActive)}>
                <span style={{ fontSize: '15px' }}>{item.emoji}</span>
                {item.label}
              </NavLink>
              {item.children?.map(child => (
                <NavLink
                  key={child.id}
                  to={child.path}
                  style={subNavStyle}
                  onMouseEnter={e => { e.currentTarget.style.color = '#3a3a3c'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#6b6b6e'; }}
                >
                  ↳ {child.label}
                </NavLink>
              ))}
            </React.Fragment>
          ))}
          {adminItems.length > 0 && (
            <>
              {!adminMode && <hr style={{ border: 'none', borderTop: '1px solid rgba(0,0,0,0.06)', margin: '8px 0' }} />}
              {adminItems.map(item => (
                <NavLink key={item.id} to={item.path} style={({ isActive }) => navStyle(isActive)}>
                  <span style={{ fontSize: '15px' }}>{item.emoji}</span>
                  {item.label}
                </NavLink>
              ))}
            </>
          )}
          {tenantAdminItems.length > 0 && (
            <>
              <hr style={{ border: 'none', borderTop: '1px solid rgba(0,0,0,0.06)', margin: '8px 0' }} />
              {tenantAdminItems.map(item => (
                <NavLink key={item.id} to={item.path} style={({ isActive }) => navStyle(isActive)}>
                  <span style={{ fontSize: '15px' }}>{item.emoji}</span>
                  {item.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>
      </aside>
    </>
  );
}
